// src/pages/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { intervalLabel, intervalBadgeClass, timeAgo } from '../utils/helpers';

// ── SIDEBAR NAVIGATION (tablet + desktop) ──
export function Sidebar({ active }) {
  const navigate = useNavigate();
  const { staff, logout, isAdmin, isSupervisor } = useAuth();
  const [pendingCount, setPendingCount] = React.useState(0);

  React.useEffect(() => {
    const fetchPending = async () => {
      try {
        const [ir, lr, dr] = await Promise.all([
          api.get('/interval-requests').catch(() => ({ data: [] })),
          api.get('/loc-requests').catch(() => ({ data: [] })),
          api.get('/discharge-requests').catch(() => ({ data: [] })),
        ]);
        const total =
          (ir.data || []).filter(r => r.status === 'pending').length +
          (lr.data || []).filter(r => r.status === 'pending').length +
          (dr.data || []).filter(r => r.status === 'pending').length;
        setPendingCount(total);
      } catch {}
    };
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, []);

  const items = [
    { id: 'home', label: 'Home', path: '/', icon: HomeIcon },
    { id: 'log', label: 'Check Log', path: '/log', icon: LogIcon },
    { id: 'requests', label: 'Requests', path: '/interval-requests', icon: RequestsIcon, badge: pendingCount > 0 },
    { id: 'schedule', label: 'Schedule', path: '/schedule', icon: ScheduleIcon },
    { id: 'searches', label: 'Room Searches', path: '/searches', icon: SearchesIcon },
    { id: 'clients', label: 'Clients', path: '/clients', icon: ClientsIcon },
    ...(isAdmin ? [
      { id: 'insights', label: 'Insights', path: '/insights', icon: InsightsIcon },
      { id: 'admin', label: 'Admin', path: '/admin', icon: AdminIcon },
      { id: 'check-options', label: 'Check Options', path: '/check-options', icon: OptionsIcon },
    ] : isSupervisor ? [
      { id: 'insights', label: 'Insights', path: '/insights', icon: InsightsIcon },
      { id: 'admin', label: 'Admin', path: '/admin', icon: AdminIcon },
    ] : []),
  ];

  const initials = `${staff?.firstName?.[0] || ''}${staff?.lastName?.[0] || ''}`;

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <img src="/numa_logo_header.png" alt="Numa" style={{ width: '85%', maxWidth: 180, height: 'auto' }} onClick={() => navigate('/')} />
        <div className="sidebar-subtitle">Digital Rounds</div>
      </div>

      <nav className="sidebar-nav">
        {items.map(item => (
          <button
            key={item.id}
            className={`sidebar-item ${active === item.id ? 'active' : ''}`}
            onClick={() => navigate(item.path)}
          >
            <item.icon />
            {item.label}
            {item.badge && <span className="sidebar-badge" />}
          </button>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user" onClick={() => navigate('/profile')}>
          <div className="sidebar-avatar">{initials}</div>
          <div>
            <div style={{ fontWeight: 500, fontSize: 13, color: '#fff' }}>{staff?.firstName} {staff?.lastName}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{staff?.role?.toUpperCase()}</div>
          </div>
        </div>
        <button
          onClick={logout}
          style={{ width: '100%', marginTop: 8, padding: '8px', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 6, color: 'rgba(255,255,255,0.6)', fontSize: 12, cursor: 'pointer', textAlign: 'center' }}
        >
          Sign out
        </button>
      </div>
    </aside>
  );
}

// ── BOTTOM NAV (mobile only) ──
export function BottomNav({ active }) {
  const navigate = useNavigate();
  const { isSupervisor, isAdmin } = useAuth();
  const [pendingCount, setPendingCount] = React.useState(0);

  React.useEffect(() => {
    const fetchPending = async () => {
      try {
        const [ir, lr, dr] = await Promise.all([
          api.get('/interval-requests').catch(() => ({ data: [] })),
          api.get('/loc-requests').catch(() => ({ data: [] })),
          api.get('/discharge-requests').catch(() => ({ data: [] })),
        ]);
        const total =
          (ir.data || []).filter(r => r.status === 'pending').length +
          (lr.data || []).filter(r => r.status === 'pending').length +
          (dr.data || []).filter(r => r.status === 'pending').length;
        setPendingCount(total);
      } catch {}
    };
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, []);

  const items = [
    { id: 'home', label: 'Home', path: '/', icon: HomeIcon },
    { id: 'log', label: 'Log', path: '/log', icon: LogIcon },
    { id: 'requests', label: 'Requests', path: '/interval-requests', icon: RequestsIcon, badge: pendingCount > 0 },
    { id: 'searches', label: 'Searches', path: '/searches', icon: SearchesIcon },
    { id: 'clients', label: 'Clients', path: '/clients', icon: ClientsIcon },
    ...(isAdmin ? [{ id: 'admin', label: 'Admin', path: '/admin', icon: AdminIcon }] : 
        isSupervisor ? [{ id: 'admin', label: 'Admin', path: '/admin', icon: AdminIcon }] : []),
    { id: 'profile', label: 'Profile', path: '/profile', icon: ProfileIcon },
  ];

  return (
    <nav className="bottom-nav">
      {items.map(item => (
        <button key={item.id} className={`nav-item ${active === item.id ? 'active' : ''}`} onClick={() => navigate(item.path)} style={{ position: 'relative' }}>
          <item.icon />
          {item.label}
          {item.badge && <span style={{ position: 'absolute', top: 4, right: '25%', width: 8, height: 8, borderRadius: '50%', background: '#F9B08F', border: '1.5px solid #fff' }} />}
        </button>
      ))}
    </nav>
  );
}

// ── PAGE WRAPPER — includes sidebar + mobile header ──
export function PageWrapper({ active, title, children, actions }) {
  const { staff, logout } = useAuth();
  const navigate = useNavigate();
  const initials = `${staff?.firstName?.[0] || ''}${staff?.lastName?.[0] || ''}`;

  return (
    <div className="app-shell">
      <Sidebar active={active} />
      <div className="main-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Mobile header */}
        <div className="mobile-header">
          <img src="/numa_logo_header.png" alt="Numa" style={{ height: 28, width: 'auto', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
          <div className="mobile-header-right">
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{staff?.firstName}</span>
            <button onClick={() => navigate('/profile')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{initials}</button>
          </div>
        </div>
        <div className="accent-stripe" />

        <div className="page-content with-nav">
          {title && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, paddingBottom: 16, borderBottom: 'var(--border)' }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--primary)' }}>{title}</h1>
              {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
            </div>
          )}
          {children}
        </div>

        <BottomNav active={active} />
      </div>
    </div>
  );
}

// ── DASHBOARD PAGE ──
export default function Dashboard() {
  const { staff, isSupervisor } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState({ today_total: 0, today_late: 0 });
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [clientRes, summaryRes] = await Promise.all([
        api.get('/clients'),
        api.get('/checks/today-summary')
      ]);
      setClients(clientRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Dashboard load error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000); // poll every 15s for fast updates
    // Also refresh when window/tab regains focus (e.g., coming back from CheckForm)
    const onFocus = () => loadData();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [loadData]);

  // Time-bucketed sections — sorted by urgency within each bucket
  const overdue  = clients.filter(c => c.isOverdue).sort((a, b) => b.minutesOverdue - a.minutesOverdue);
  const next15   = clients.filter(c => !c.isOverdue && c.minutesUntilDue <= 15).sort((a, b) => a.minutesUntilDue - b.minutesUntilDue);
  const next60   = clients.filter(c => !c.isOverdue && c.minutesUntilDue > 15 && c.minutesUntilDue <= 60).sort((a, b) => a.minutesUntilDue - b.minutesUntilDue);
  const next2hrs = clients.filter(c => !c.isOverdue && c.minutesUntilDue > 60 && c.minutesUntilDue <= 120).sort((a, b) => a.minutesUntilDue - b.minutesUntilDue);
  const later    = clients.filter(c => !c.isOverdue && c.minutesUntilDue > 120).sort((a, b) => a.minutesUntilDue - b.minutesUntilDue);

  const Section = ({ label, color, clients: list, urgent }) => list.length === 0 ? null : (
    <>
      <div className="section-label" style={{ color: color || 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        {urgent && <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />}
        {label} <span style={{ fontWeight: 400, color: '#9CA3AF', fontSize: 11 }}>({list.length})</span>
      </div>
      <div className="card-grid" style={{ marginBottom: 4 }}>
        {list.map(client => (
          <ClientCard key={client.id} client={client} onCheck={() => navigate(`/check/${client.id}`)} overdue={client.isOverdue} />
        ))}
      </div>
    </>
  );

  return (
    <PageWrapper active="home" title="Digital Rounds">
      <div className="stats-grid">
        <div className={`stat-card ${overdue.length > 0 ? 'danger' : ''}`}>
          <div className="num">{overdue.length}</div>
          <div className="lbl">Overdue</div>
        </div>
        <div className="stat-card">
          <div className="num">{clients.length}</div>
          <div className="lbl">Active clients</div>
        </div>
        <div className="stat-card">
          <div className="num">{summary.today_total || 0}</div>
          <div className="lbl">Checks today</div>
        </div>
        <div className="stat-card">
          <div className="num">{summary.today_late || 0}</div>
          <div className="lbl">Late today</div>
        </div>
      </div>

      {overdue.length > 0 && (
        <div className="alert-bar danger">
          {overdue.length} client{overdue.length > 1 ? 's are' : ' is'} overdue — check now.
        </div>
      )}

      {loading ? <div className="spinner" /> : clients.length === 0 ? (
        <div className="empty-state">
          No active clients.<br />
          <span style={{ color: 'var(--primary)', cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate('/clients')}>
            Add a client →
          </span>
        </div>
      ) : (
        <>
          <Section label="Overdue now" color="#A32D2D" clients={overdue} urgent />
          {overdue.length > 0 && (next15.length > 0 || next60.length > 0) && <div className="divider" />}
          <Section label="Due in next 15 min" color="var(--accent-dark)" clients={next15} urgent />
          <Section label="Due in next hour" color="var(--primary)" clients={next60} />
          <Section label="Due in 1–2 hours" clients={next2hrs} />
          <Section label="Later" clients={later} />
        </>
      )}
    </PageWrapper>
  );
}

function ClientCard({ client, onCheck, overdue }) {
  const nextDue = new Date(client.nextCheckDue);
  const now = new Date();
  const minsUntil = Math.round((nextDue - now) / 60000);

  return (
    <div className="card" style={{ borderLeft: overdue ? '3px solid var(--red)' : '3px solid var(--primary)', borderRadius: '0 12px 12px 0', marginBottom: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{client.first_name} {client.last_name}</div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
            DOB: {new Date(client.date_of_birth).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' })}
            {client.room_number && ` · Room ${client.room_number}`}
          </div>
          {client.program_type && (
            <div style={{ marginTop: 4 }}>
              <span style={{
                background: client.program_type === 'detox' ? '#FEF3C7' : '#EFF6FF',
                color: client.program_type === 'detox' ? '#92400E' : '#1E40AF',
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                border: `1px solid ${client.program_type === 'detox' ? '#FCD34D' : '#BFDBFE'}`
              }}>
                {client.program_type === 'detox'
                  ? (client.isInDetoxWindow ? `🔶 Detox · Hour ${client.detoxHoursElapsed} of 72` : '🔶 Detox')
                  : '🏠 Residential'}
              </span>
            </div>
          )}
        </div>
        <span className={intervalBadgeClass(client.current_interval_minutes)}>
          {intervalLabel(client.current_interval_minutes)}
        </span>
      </div>
      <div style={{ fontSize: 12, color: overdue ? '#A32D2D' : '#4B5563', marginBottom: 10, fontWeight: overdue ? 500 : 400 }}>
        {overdue
          ? `Overdue by ${client.minutesOverdue} min · Last: ${client.last_check_datetime ? timeAgo(client.last_check_datetime) : 'never'}`
          : `Next: ${minsUntil > 0 ? `in ${minsUntil} min` : 'now'} · Last: ${client.last_check_datetime ? timeAgo(client.last_check_datetime) : 'never'}`
        }
        {client.last_check_staff && ` · ${client.last_check_staff}`}
      </div>
      <button
        className={`btn btn-sm ${overdue ? 'btn-danger' : 'btn-primary'}`}
        style={{ width: '100%' }}
        onClick={onCheck}
      >
        {overdue ? '⚠ Log check now' : 'Log check'}
      </button>
    </div>
  );
}

// Icons
const HomeIcon = () => (<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>);
const LogIcon = () => (<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>);
const RequestsIcon = () => (<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
const ScheduleIcon = () => (<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>);
const ClientsIcon = () => (<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>);
const AdminIcon = () => (<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>);
const OptionsIcon = () => (<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>);
const ProfileIcon = () => (<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>);
const InsightsIcon = () => (<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18" /><path strokeLinecap="round" strokeLinejoin="round" d="M7 15l4-5 3 3 5-7" /></svg>);
const SearchesIcon = () => (<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16" /></svg>);
